import type { CourseAttachment } from "@/api/generated/model";
import { useState } from "react";
import { Folder, Download, Loader2 } from "lucide-react";
import {
  headerButtonClassName,
  headerMenuEnterStyle,
  headerStackedToolClassName,
  headerToolClassName,
} from "@/components/layout/headerButtonClassName";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { formatBytes } from "@/lib/utils";
import { useEnrollmentAttachments } from "@/hooks/attachments/useEnrollmentAttachments";
import { useHeaderMenuFocus } from "@/hooks/useHeaderMenuFocus";

interface Props {
  enrollmentId: string;
}

interface AttachmentsListProps {
  attachments: CourseAttachment[];
  isLoading: boolean;
  error: unknown;
}

const AttachmentsList = ({
  attachments,
  isLoading,
  error,
}: AttachmentsListProps) => {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-2 py-3 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" />
        Wird geladen…
      </div>
    );
  }

  if (error) {
    return (
      <p className="px-2 py-3 text-sm text-destructive">
        Dokumente konnten nicht geladen werden.
      </p>
    );
  }

  if (attachments.length === 0) {
    return (
      <p className="px-2 py-3 text-sm text-muted-foreground">
        Keine Dokumente vorhanden.
      </p>
    );
  }

  return (
    <ul className="flex flex-col">
      {attachments.map((attachment) => (
        <li key={attachment.id}>
          <a
            href={attachment.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 rounded-md px-2 py-2 text-sm hover:bg-accent"
          >
            <Download className="size-4 shrink-0 text-muted-foreground" />
            <span className="min-w-0 flex-1 truncate" title={attachment.title}>
              {attachment.title}
            </span>
            <span className="shrink-0 text-xs text-muted-foreground">
              {formatBytes(attachment.byteSize)}
            </span>
          </a>
        </li>
      ))}
    </ul>
  );
};

export const AttachmentsHeaderButton = ({ enrollmentId }: Props) => {
  const [open, setOpen] = useState(false);
  const { notePointerOpen, noteKeyboardOpen, keepTriggerFocused } =
    useHeaderMenuFocus();

  // Fetched lazily, once the popover is opened.
  const {
    attachments: loaded,
    error,
    isLoading,
  } = useEnrollmentAttachments(enrollmentId, open);

  const attachments = loaded
    .slice()
    .sort((a, b) =>
      a.title.localeCompare(b.title, "de", { sensitivity: "base" })
    );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          aria-label="Dokumente"
          className={[
            "border-transparent text-primary-foreground",
            headerButtonClassName,
            headerStackedToolClassName,
            headerToolClassName(open),
          ].join(" ")}
          onPointerDown={notePointerOpen}
          onKeyDown={noteKeyboardOpen}
        >
          <Folder className="size-4" />
          Dokumente
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="end"
        sideOffset={8}
        className="w-80 p-2 data-[state=closed]:pointer-events-none"
        style={headerMenuEnterStyle}
        onOpenAutoFocus={keepTriggerFocused}
        onCloseAutoFocus={keepTriggerFocused}
      >
        <p className="px-2 py-1.5 text-sm font-semibold">Dokumente</p>
        <AttachmentsList
          attachments={attachments}
          isLoading={isLoading}
          error={error}
        />
      </PopoverContent>
    </Popover>
  );
};
